import { LEVEL } from '../hooks/usePermission';
import type { NavCondition } from './navConfig';

/**
 * Menus > Menu Cycles has three pages (Jeff, 2026-11-04: "too many options"): Menu Cycles, Menu Changes
 * and New Menu. Menu Changes and New Menu each hold what used to be separate pages, as tabs named in
 * the address (`?tab=`), and every old address still opens the right tab (movedMenuAddress).
 *
 * The tabs and their gates live here, in one place, so the menu entry (navConfig.ts), the page that
 * draws the tabs and the old addresses cannot disagree.
 */

export const MENU_CHANGES_ROUTE = '/menucycle/changes';
export const NEW_MENU_ROUTE = '/menucycle/new';
/** A saved vendor import: `/menucycle/new/import/:draftId/:stage?` (was /menucycle/import/:draftId/:stage?). */
export const NEW_MENU_IMPORT_ROUTE = `${NEW_MENU_ROUTE}/import`;

/** What a tab needs, in the menu's own terms (NavPageConfig). */
export interface MenuTabGate {
  securityField: string;
  minLevel?: number;
  psiOnly?: boolean;
  when?: NavCondition[];
}

export type MenuChangesTab = 'ask' | 'sheets' | 'ai';
export type NewMenuTab = 'import' | 'draft' | 'memory';

export interface MenuTab<T extends string> {
  id: T;
  label: string;
  /** Not on a phone: the page edits menus (App.tsx notOnPhone). */
  notOnPhone?: boolean;
}

/**
 * Menu Changes. Each tab keeps the gate its page had: asking PSI is Menu Cycle level 1 (it edits
 * nothing, so it stays open on a phone); applying returned extension sheets is Holiday Overrides
 * level 3, as Swap; changing a menu with AI is PSI staff only, as the API's MenuChangeController.
 * The first two are for production facilities, as their menu entries were.
 */
export const MENU_CHANGES_TABS: (MenuTab<MenuChangesTab> & { gate: MenuTabGate })[] = [
  { id: 'ask', label: 'Ask PSI', gate: { securityField: 'dietech_Menu_Cycle_Level', when: ['production'] } },
  {
    id: 'sheets',
    label: 'From extension sheets',
    notOnPhone: true,
    gate: { securityField: 'dietech_Menu_Cycle_Holiday_Overrides_Level', minLevel: LEVEL.FULL, when: ['production'] },
  },
  { id: 'ai', label: 'Change with AI', notOnPhone: true, gate: { securityField: 'dietech_Menu_Cycle_Level', psiOnly: true } },
];

/** New Menu: PSI staff only as a whole, the vendor import included (Jeff, 2026-11-04). */
export const NEW_MENU_TABS: MenuTab<NewMenuTab>[] = [
  { id: 'import', label: "Import a vendor's menu" },
  { id: 'draft', label: 'Draft with AI' },
  { id: 'memory', label: 'What the AI remembers' },
];

/** `?tab=` when it names one of `allowed`, else the first allowed tab (null when there is none). */
export function chosenTab<T extends string>(asked: string | null, allowed: readonly T[]): T | null {
  return allowed.find((t) => t === asked) ?? allowed[0] ?? null;
}

/** `path?tab=<tab>&<every other parameter of search>`. */
function withTab(path: string, tab: string, search: string): string {
  const params = new URLSearchParams();
  params.set('tab', tab);
  new URLSearchParams(search).forEach((value, key) => {
    if (key !== 'tab') params.append(key, value);
  });
  return `${path}?${params.toString()}`;
}

/**
 * Where an old menu address opens now, with every parameter it carried (the facility handoff's
 * openConnection/openFacility/signin, the builder's template, request and menu): bookmarks, pins and
 * Tech Log's links keep working. Null for an address that did not move.
 *
 * /menucycle/builder was both drafting and changing a menu: Tech Log's "Work this with AI" names the
 * change (`?request=` and/or `?menu=`), so it opens Menu Changes > Change with AI; anything else opens
 * New Menu > Draft with AI.
 */
export function movedMenuAddress(pathname: string, search: string): string | null {
  const path = pathname.replace(/\/+$/, '');
  const params = new URLSearchParams(search);
  switch (path) {
    case '/menucycle/requests':
      return withTab(MENU_CHANGES_ROUTE, 'sheets', search);
    case '/menucycle/request-change':
    case '/menucycle/changerequest':
      return withTab(MENU_CHANGES_ROUTE, 'ask', search);
    case '/menucycle/import':
      return withTab(NEW_MENU_ROUTE, 'import', search);
    case '/menucycle/builder':
      return params.has('request') || params.has('menu')
        ? withTab(MENU_CHANGES_ROUTE, 'ai', search)
        : withTab(NEW_MENU_ROUTE, 'draft', search);
    default:
      break;
  }
  const draft = /^\/menucycle\/import\/([^/]+)(\/[^/]+)?$/.exec(path);
  if (draft) return `${NEW_MENU_IMPORT_ROUTE}/${draft[1]}${draft[2] ?? ''}${search}`;
  return null;
}
